/**
 * Client-side cryptographic operations for E2E Encrypted File Delivery.
 * Uses Web Crypto API (SubtleCrypto) only.
 *
 * Security rules:
 * - DSEARCH-RULE-03: Plaintext file content MUST NOT be persisted into localStorage/Zustand.
 * - Private key is memory-only and never serialized or stored in component state.
 */

/**
 * Generate an ephemeral ECDH P-256 key pair for the requester.
 * Public key sent to backend in SPKI PEM format; private key kept in memory only.
 */
export async function generateEphemeralKeyPair(): Promise<{
  publicKeyPem: string;
  privateKey: CryptoKey;
}> {
  const keyPair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    // Private key is non-extractable. WebCrypto always keeps the public key of a
    // generated pair exportable, so SPKI export below still works.
    false,
    ['deriveKey', 'deriveBits'],
  );

  // Export public key as SPKI → PEM
  const spki = await crypto.subtle.exportKey('spki', keyPair.publicKey);
  const bytes = new Uint8Array(spki);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  const chunks = base64.match(/.{1,64}/g) ?? [base64];
  const publicKeyPem = `-----BEGIN PUBLIC KEY-----\n${chunks.join('\n')}\n-----END PUBLIC KEY-----`;

  return {
    publicKeyPem,
    privateKey: keyPair.privateKey,
  };
}

/**
 * Derive AES-256-GCM session key from ECDH shared secret using HKDF-SHA256.
 * Info label: "dsearch-file-key".
 */
export async function deriveSessionKey(
  myPrivateKey: CryptoKey,
  peerPublicKeyPem: string,
): Promise<CryptoKey> {
  const peerBase64 = peerPublicKeyPem
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s/g, '');

  const binaryDerString = atob(peerBase64);
  const spkiBytes = new Uint8Array(binaryDerString.length);
  for (let i = 0; i < binaryDerString.length; i++) {
    spkiBytes[i] = binaryDerString.charCodeAt(i);
  }

  const peerPublicKey = await crypto.subtle.importKey(
    'spki',
    spkiBytes.buffer,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );

  // ECDH → 256-bit shared secret
  const sharedSecret = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: peerPublicKey },
    myPrivateKey,
    256,
  );

  // HKDF → AES-256-GCM key
  const hkdfKey = await crypto.subtle.importKey(
    'raw',
    sharedSecret,
    'HKDF',
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0),
      info: new TextEncoder().encode('dsearch-file-key'),
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt', 'encrypt'],
  );
}

/**
 * Decrypt AES-256-GCM ciphertext.
 * DSEARCH-RULE-03: Plaintext MUST be consumed immediately (trigger download),
 * never stored in React state or localStorage.
 */
export async function decryptFile(
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  sessionKey: CryptoKey,
): Promise<Uint8Array> {
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce as unknown as BufferSource },
    sessionKey,
    ciphertext as unknown as BufferSource,
  );
  return new Uint8Array(plaintext);
}

/**
 * Helper to encrypt plaintext with AES-256-GCM (used for test verification).
 */
export async function encryptFile(
  plaintext: Uint8Array,
  nonce: Uint8Array,
  sessionKey: CryptoKey,
): Promise<Uint8Array> {
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce as unknown as BufferSource },
    sessionKey,
    plaintext as unknown as BufferSource,
  );
  return new Uint8Array(ciphertext);
}

/**
 * Trigger browser file download from plaintext bytes.
 * Immediately revokes object URL after click.
 */
export function triggerDownload(
  data: Uint8Array,
  filename: string,
  mimeType = 'application/octet-stream',
): void {
  const blob = new Blob([data as unknown as BlobPart], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Shared AES-GCM / HKDF primitives (TASK-UI-VFS-003-1), re-exported for reuse. */
export {
  encryptContent as aesGcmEncrypt,
  decryptContent as aesGcmDecrypt,
} from '@/shared/lib/vfs-crypto/envelope';
export { deriveWorkspaceKey } from '@/shared/lib/vfs-crypto/hkdf';
